import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { FiPlus, FiEdit2, FiTrash2, FiSend, FiCheck, FiDollarSign, FiFileText, FiLock } from 'react-icons/fi';
import viaticosService from '../services/viaticosService';
import Loading from '../components/Loading';
import './ViaticosPage.css';

const ensureArray = (v) => Array.isArray(v) ? v : [];

const ViaticosPage = () => {
  const [solicitudes, setSolicitudes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [rendicionModal, setRendicionModal] = useState({ open: false, solicitud: null });
  const [editing, setEditing] = useState(null);
  const [filtroEstado, setFiltroEstado] = useState('');

  const [form, setForm] = useState({
    motivo: '', destino: '', fecha_inicio: '', fecha_fin: '',
    monto_estimado: '', proyecto: '', centro_costo: '',
    plan_cuenta_anticipo: '', plan_cuenta_gasto: '',
  });

  const [rendicionItems, setRendicionItems] = useState([{ fecha: '', concepto: '', monto: '', tiene_boleta: false }]);

  useEffect(() => { cargarDatos(); }, []);

  const cargarDatos = async () => {
    try {
      setLoading(true);
      const filters = {};
      if (filtroEstado) filters.estado = filtroEstado;
      const res = await viaticosService.getAll(filters);
      setSolicitudes(ensureArray(res.data.results || res.data));
    } catch { toast.error('Error al cargar viaticos'); }
    finally { setLoading(false); }
  };

  useEffect(() => { cargarDatos(); }, [filtroEstado]);

  const handleSave = async () => {
    try {
      const data = { ...form };
      if (!data.proyecto) delete data.proyecto;
      if (!data.centro_costo) delete data.centro_costo;
      if (!data.plan_cuenta_anticipo) delete data.plan_cuenta_anticipo;
      if (!data.plan_cuenta_gasto) delete data.plan_cuenta_gasto;
      if (editing) {
        await viaticosService.update(editing.id, data);
        toast.success('Solicitud actualizada');
      } else {
        await viaticosService.create(data);
        toast.success('Solicitud creada');
      }
      setModalOpen(false); setEditing(null); cargarDatos();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error al guardar'); }
  };

  const handleAction = async (id, action, label) => {
    if (!window.confirm(`¿${label} esta solicitud?`)) return;
    try {
      await viaticosService[action](id);
      toast.success(`Solicitud ${label.toLowerCase()}da`);
      cargarDatos();
    } catch (err) { toast.error(err.response?.data?.detail || `Error al ${label.toLowerCase()}`); }
  };

  const handleRendir = async () => {
    const sol = rendicionModal.solicitud;
    if (!sol) return;
    try {
      await viaticosService.rendir(sol.id, { detalles: rendicionItems.filter(i => i.concepto && i.monto) });
      toast.success('Rendicion registrada');
      setRendicionModal({ open: false, solicitud: null });
      setRendicionItems([{ fecha: '', concepto: '', monto: '', tiene_boleta: false }]);
      cargarDatos();
    } catch (err) { toast.error(err.response?.data?.detail || 'Error al rendir'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar esta solicitud?')) return;
    try { await viaticosService.delete(id); toast.success('Eliminada'); cargarDatos(); }
    catch { toast.error('Error al eliminar'); }
  };

  const handleEdit = (s) => {
    setEditing(s);
    setForm({ motivo: s.motivo, destino: s.destino, fecha_inicio: s.fecha_inicio, fecha_fin: s.fecha_fin, monto_estimado: s.monto_estimado, proyecto: s.proyecto || '', centro_costo: s.centro_costo || '', plan_cuenta_anticipo: s.plan_cuenta_anticipo || '', plan_cuenta_gasto: s.plan_cuenta_gasto || '' });
    setModalOpen(true);
  };

  const resetForm = () => setForm({ motivo: '', destino: '', fecha_inicio: '', fecha_fin: '', monto_estimado: '', proyecto: '', centro_costo: '', plan_cuenta_anticipo: '', plan_cuenta_gasto: '' });

  const formatCurrency = (v) => new Intl.NumberFormat('es-CL', { style: 'currency', currency: 'CLP' }).format(v || 0);

  const getEstadoColor = (estado) => {
    const map = {
      borrador: '#6b7280', pendiente: '#f59e0b', aprobado: '#059669',
      rechazado: '#dc2626', entregado: '#0891b2', rendido: '#1e40af', cerrado: '#374151',
    };
    return map[estado] || '#6b7280';
  };

  const getEstadoBadge = (estado) => {
    const map = {
      borrador: 'badge-gray', pendiente: 'badge-yellow', aprobado: 'badge-green',
      rechazado: 'badge-red', entregado: 'badge-cyan', rendido: 'badge-blue', cerrado: 'badge-dark',
    };
    return map[estado] || 'badge-gray';
  };

  const addRendicionItem = () => setRendicionItems([...rendicionItems, { fecha: '', concepto: '', monto: '', tiene_boleta: false }]);

  if (loading) return <Loading />;

  return (
    <div className="viaticos-page">
      <div className="page-header">
        <h1>Viaticos y Fondos por Rendir</h1>
        <button className="btn-primary" onClick={() => { resetForm(); setEditing(null); setModalOpen(true); }}><FiPlus /> Nueva Solicitud</button>
      </div>

      {/* Workflow visual */}
      <div className="workflow-bar">
        {['borrador', 'pendiente', 'aprobado', 'entregado', 'rendido', 'cerrado'].map((e, i) => (
          <React.Fragment key={e}>
            <div className="workflow-step" style={{ background: getEstadoColor(e) }}>
              {e.charAt(0).toUpperCase() + e.slice(1)}
              <span className="workflow-count">{solicitudes.filter(s => s.estado === e).length}</span>
            </div>
            {i < 5 && <span className="workflow-arrow">→</span>}
          </React.Fragment>
        ))}
      </div>

      <div className="filtros-container">
        <select className="select-filtro" value={filtroEstado} onChange={e => setFiltroEstado(e.target.value)}>
          <option value="">Todos los estados</option>
          <option value="borrador">Borrador</option>
          <option value="pendiente">Pendiente</option>
          <option value="aprobado">Aprobado</option>
          <option value="entregado">Entregado</option>
          <option value="rendido">Rendido</option>
          <option value="cerrado">Cerrado</option>
        </select>
      </div>

      <div className="table-container">
        <table className="data-table">
          <thead>
            <tr>
              <th>Solicitante</th><th>Motivo</th><th>Destino</th><th>Fechas</th>
              <th style={{ textAlign: 'right' }}>Estimado</th><th style={{ textAlign: 'right' }}>Entregado</th><th style={{ textAlign: 'right' }}>Rendido</th>
              <th>Estado</th><th style={{ textAlign: 'right' }}>Acciones</th>
            </tr>
          </thead>
          <tbody>
            {solicitudes.length === 0 ? (
              <tr><td colSpan="9" style={{ textAlign: 'center', padding: 40, color: '#6b7280' }}>Sin solicitudes</td></tr>
            ) : solicitudes.map(s => (
              <tr key={s.id}>
                <td>{s.solicitante_nombre || s.solicitante || '-'}</td>
                <td>{s.motivo}</td>
                <td>{s.destino}</td>
                <td>{s.fecha_inicio} - {s.fecha_fin}</td>
                <td className="mono right">{formatCurrency(s.monto_estimado)}</td>
                <td className="mono right">{formatCurrency(s.monto_entregado || 0)}</td>
                <td className="mono right">{formatCurrency(s.monto_rendido || 0)}</td>
                <td><span className={`badge ${getEstadoBadge(s.estado)}`} style={{ borderLeft: `3px solid ${getEstadoColor(s.estado)}` }}>{s.estado_display || s.estado}</span></td>
                <td>
                  <div className="acciones-cell">
                    {s.estado === 'borrador' && (
                      <>
                        <button className="btn-icon" title="Enviar" onClick={() => handleAction(s.id, 'enviar', 'Enviar')}><FiSend /></button>
                        <button className="btn-icon" title="Editar" onClick={() => handleEdit(s)}><FiEdit2 /></button>
                        <button className="btn-icon btn-danger" onClick={() => handleDelete(s.id)}><FiTrash2 /></button>
                      </>
                    )}
                    {s.estado === 'pendiente' && (
                      <button className="btn-icon btn-success" title="Aprobar" onClick={() => handleAction(s.id, 'aprobar', 'Aprobar')}><FiCheck /></button>
                    )}
                    {s.estado === 'aprobado' && (
                      <button className="btn-icon" title="Entregar Fondo" onClick={() => handleAction(s.id, 'entregarFondo', 'Entregar fondo')} style={{ background: '#e0f2fe', color: '#0369a1' }}><FiDollarSign /></button>
                    )}
                    {s.estado === 'entregado' && (
                      <button className="btn-icon" title="Rendir" onClick={() => { setRendicionModal({ open: true, solicitud: s }); setRendicionItems([{ fecha: '', concepto: '', monto: '', tiene_boleta: false }]); }} style={{ background: '#dbeafe', color: '#1e40af' }}><FiFileText /></button>
                    )}
                    {s.estado === 'rendido' && (
                      <button className="btn-icon" title="Cerrar" onClick={() => handleAction(s.id, 'cerrar', 'Cerrar')} style={{ background: '#f3f4f6', color: '#374151' }}><FiLock /></button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Modal Crear/Editar */}
      {modalOpen && (
        <div className="modal-overlay" onClick={() => setModalOpen(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <h2>{editing ? 'Editar Solicitud' : 'Nueva Solicitud de Viatico'}</h2>
            <div className="form-grid">
              <div className="form-group" style={{ gridColumn: 'span 2' }}><label>Motivo *</label><input value={form.motivo} onChange={e => setForm({ ...form, motivo: e.target.value })} placeholder="Motivo del viaje" /></div>
              <div className="form-group"><label>Destino *</label><input value={form.destino} onChange={e => setForm({ ...form, destino: e.target.value })} placeholder="Ciudad/Lugar" /></div>
              <div className="form-group"><label>Monto Estimado *</label><input type="number" value={form.monto_estimado} onChange={e => setForm({ ...form, monto_estimado: e.target.value })} /></div>
              <div className="form-group"><label>Fecha Inicio</label><input type="date" value={form.fecha_inicio} onChange={e => setForm({ ...form, fecha_inicio: e.target.value })} /></div>
              <div className="form-group"><label>Fecha Fin</label><input type="date" value={form.fecha_fin} onChange={e => setForm({ ...form, fecha_fin: e.target.value })} /></div>
            </div>
            <div className="modal-actions">
              <button className="btn-secondary" onClick={() => setModalOpen(false)}>Cancelar</button>
              <button className="btn-primary" onClick={handleSave}>{editing ? 'Actualizar' : 'Crear'}</button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Rendicion */}
      {rendicionModal.open && (
        <div className="modal-overlay" onClick={() => setRendicionModal({ open: false, solicitud: null })}>
          <div className="modal-content modal-lg" onClick={e => e.stopPropagation()}>
            <h2>Rendicion de Gastos</h2>
            <div className="rendicion-resumen">
              <span>Entregado: <strong>{formatCurrency(rendicionModal.solicitud?.monto_entregado)}</strong></span>
              <span>Rendido: <strong>{formatCurrency(rendicionItems.reduce((s, i) => s + parseFloat(i.monto || 0), 0))}</strong></span>
            </div>
            <table className="data-table" style={{ marginBottom: 12 }}>
              <thead><tr><th>Fecha</th><th>Concepto</th><th>Monto</th><th>Boleta</th><th style={{ width: 40 }}></th></tr></thead>
              <tbody>
                {rendicionItems.map((item, i) => (
                  <tr key={i}>
                    <td><input type="date" value={item.fecha} onChange={e => { const items = [...rendicionItems]; items[i].fecha = e.target.value; setRendicionItems(items); }} style={{ padding: '6px 8px', border: '1px solid #d1d5db', borderRadius: 4, fontSize: 13 }} /></td>
                    <td><input value={item.concepto} onChange={e => { const items = [...rendicionItems]; items[i].concepto = e.target.value; setRendicionItems(items); }} placeholder="Concepto" style={{ padding: '6px 8px', border: '1px solid #d1d5db', borderRadius: 4, fontSize: 13, width: '100%' }} /></td>
                    <td><input type="number" value={item.monto} onChange={e => { const items = [...rendicionItems]; items[i].monto = e.target.value; setRendicionItems(items); }} style={{ padding: '6px 8px', border: '1px solid #d1d5db', borderRadius: 4, fontSize: 13, width: 100 }} /></td>
                    <td><input type="checkbox" checked={item.tiene_boleta} onChange={e => { const items = [...rendicionItems]; items[i].tiene_boleta = e.target.checked; setRendicionItems(items); }} /></td>
                    <td><button className="btn-icon btn-danger" onClick={() => setRendicionItems(rendicionItems.filter((_, j) => j !== i))} style={{ width: 24, height: 24 }}>×</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
            <button className="btn-secondary" onClick={addRendicionItem} style={{ marginBottom: 16 }}>+ Agregar linea</button>
            <div className="modal-actions">
              <button className="btn-secondary" onClick={() => setRendicionModal({ open: false, solicitud: null })}>Cancelar</button>
              <button className="btn-primary" onClick={handleRendir}>Registrar Rendicion</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ViaticosPage;
